import { Component, EventEmitter, NgZone, Output, ViewChild, ElementRef } from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { MapComponent } from '../map/map.component';

import { RequestMethodsService } from '@services/request/request-methods.service';
import { CityService } from '@services/city/city.service';
import { AuthService } from '@services/auth/auth.service';
import { IsInGeofenceService } from '@services/is-in-geofence/is-in-geofence.service';

import { FullAddressLongLatInterface } from '@interfaces/orders/address.interface';
import { FavoriteAddressInterface } from '@interfaces/orders/favorite-address.interface';
import { CityInterface, CityPaginationInterface } from '@interfaces/components/cities.interface';
import { EndpointConstant } from '@interfaces/global/endpoint.constant';
import { CheckToken } from '@interfaces/auth/login.interface';

@Component({
  selector: 'app-mobile-address-selection',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule, TranslateModule, MapComponent],
  templateUrl: './mobile-address-selection.component.html',
  styleUrls: ['./mobile-address-selection.component.scss']
})
export class MobileAddressSelectionComponent {
  @ViewChild('mapComponent') mapComponent!: MapComponent;
  @ViewChild('searchInputEl') searchInputEl?: ElementRef;
  @Output() confirmed = new EventEmitter<{ address: FavoriteAddressInterface } | { city: CityInterface }>();
  @Output() closed = new EventEmitter<void>();

  viewState: 'map' | 'search' = 'map';
  citySearch = false;
  locations: any[] = [];
  cities: CityInterface[] = [];
  selectedAddressOrCity!: FavoriteAddressInterface | CityInterface | FullAddressLongLatInterface | null;
  ymaps: any;
  isUser: CheckToken = CheckToken.INVALID;
  displayValue = '';

  constructor(
    private cityService: CityService,
    private requestService: RequestMethodsService,
    private authService: AuthService,
    private isInGeofenceService: IsInGeofenceService,
    private ngZone: NgZone
  ) {
    this.ymaps = (window as any)['ymaps'];
    this.isUser = this.authService.checkToken();
  }

  mapReadyHandler(): void {
    const stored = this.cityService.getCityModelFromLocalStorage();
    if (!stored) return;

    if ('address' in stored && stored.address) {
      this.selectedAddressOrCity = stored.address;
      this.displayValue = stored.address.fullAddress;
    } else if ('city' in stored && stored.city) {
      this.selectedAddressOrCity = stored.city;
      this.citySearch = true;
      this.displayValue = (stored.city as any).cityName || stored.city.name || '';
    }
    this.setInputValue(this.displayValue);
  }

  onSearchInput(value: string): void {
    if (!value) {
      this.locations = [];
      this.cities = [];
      if (this.viewState === 'search') {
        this.exitSearch();
      }
      return;
    }
    this.viewState = 'search';
    if (this.citySearch) {
      this.getCities(value);
    } else {
      this.searchAddress(value);
    }
  }

  searchAddress(text: string): void {
    if (!this.ymaps) return;
    this.ymaps.geocode(text).then((res: any) => {
      this.locations = res.geoObjects.toArray().filter((geoObject: any) => {
        const meta = geoObject.properties.get('metaDataProperty').GeocoderMetaData;
        if (meta.AddressDetails.Country?.CountryNameCode !== 'RU') return false;

        const components = meta.Address.Components;
        const street = components.find((c: any) => c.kind === 'street')?.name || '';
        const house = components.find((c: any) => c.kind === 'house')?.name || '';
        geoObject.streetAndHouse = `${street} ${house}`.trim();

        const province = components.find((c: any) => c.kind === 'province')?.name || '';
        const locality = components.find((c: any) => c.kind === 'locality')?.name || '';
        geoObject.cityAndRegion = `${province}, г. ${locality}`;

        return true;
      });
    });
  }

  getCities(search: string): void {
    this.requestService.get(
      `${EndpointConstant.cities}/${EndpointConstant.all}?page=1&size=100&search=${search}`
    ).subscribe((response: CityPaginationInterface) => {
      this.cities = response.content.filter(item => item.name);
    });
  }

  selectAddressFromSearch(geoObject: any): void {
    const components = geoObject.properties._data.metaDataProperty.GeocoderMetaData.Address.Components;
    const cityName = components.find((c: any) => c.kind === 'locality')?.name || '';
    const address = this.isInGeofenceService.fromGeObjectGetLongLatAndAddress(
      geoObject.properties.get('name'),
      geoObject.geometry._coordinates,
      cityName
    );

    if (!address) return;

    this.selectedAddressOrCity = address;
    this.displayValue = address.fullAddress;
    this.locations = [];
    this.exitSearch();

    setTimeout(() => {
      this.setInputValue(address.fullAddress);
      this.mapComponent?.map?.setCenter([address.latitude, address.longitude]);
      this.mapComponent?.map?.setZoom(18);
    }, 50);
  }

  selectCityFromSearch(city: CityInterface): void {
    this.selectedAddressOrCity = city;
    this.displayValue = city.name || '';
    this.cities = [];
    this.exitSearch();

    setTimeout(() => {
      this.setInputValue(city.name || '');
      this.mapComponent?.map?.setCenter([+city.latitude, +city.longitude]);
      this.mapComponent?.map?.setZoom(8);
    }, 50);
  }

  selectAddressFromMap(address: FullAddressLongLatInterface | null): void {
    if (this.citySearch || !address) return;
    this.ngZone.run(() => {
      this.selectedAddressOrCity = address;
      this.displayValue = address.fullAddress;
      this.setInputValue(address.fullAddress);
    });
  }

  toggleCitySearch(checked: boolean): void {
    this.citySearch = checked;
    this.locations = [];
    this.cities = [];
    this.setInputValue('');
    if (this.viewState === 'search') {
      this.exitSearch();
    }
    const stored = this.cityService.getCityModelFromLocalStorage();
    if (stored) {
      if (checked && 'city' in stored && stored.city) {
        this.mapComponent?.map?.setCenter([+stored.city.latitude, +stored.city.longitude]);
        this.mapComponent?.map?.setZoom(8);
      } else if (!checked && 'address' in stored && stored.address) {
        this.mapComponent?.map?.setCenter([stored.address.latitude, stored.address.longitude]);
        this.mapComponent?.map?.setZoom(18);
      }
    }
  }

  handleBackPress(): boolean {
    if (this.viewState === 'search') {
      this.setInputValue(this.displayValue);
      this.exitSearch();
      return true;
    }
    return false;
  }

  confirm(): void {
    if (!this.selectedAddressOrCity) return;

    if (this.isUser === CheckToken.VALID && !this.citySearch) {
      this.saveAddressAndConfirm();
    } else {
      this.confirmed.emit(
        this.citySearch
          ? { city: this.selectedAddressOrCity as CityInterface }
          : { address: this.selectedAddressOrCity as FavoriteAddressInterface }
      );
    }
  }

  saveAddressAndConfirm(): void {
    this.requestService.post(
      `${EndpointConstant.favorite}-${EndpointConstant.addresses}`,
      this.selectedAddressOrCity
    ).subscribe((response: FavoriteAddressInterface) => {
      this.confirmed.emit({ address: response });
    });
  }

  private exitSearch(): void {
    this.viewState = 'map';
    setTimeout(() => {
      this.mapComponent?.map?.container?.fitToViewport();
    }, 0);
  }

  private setInputValue(value: string): void {
    if (this.searchInputEl) {
      this.searchInputEl.nativeElement.value = value;
    }
  }
}
